import React, { CSSProperties, PropsWithChildren, useState, useEffect } from "react";

import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from '@mui/icons-material/Close';
import { useStoreDispatch } from "../../store/Hooks";
import { navSliceActions } from "../../store/slices/NavSlice";
import { enqueueSnackbar } from "notistack";

interface EditorWrapperProps {
  title?: string | undefined;
  save?: () => Promise<void>;
  delete?: () => Promise<void>;
  cancel?: () => Promise<void>;
  commit?: () => Promise<void>;
  table?: string;
  getSaveMessage?: () => string;
  getDeleteMessage?: () => string;
  inDrawer?: boolean; // whether editor is displayed in a STAND-ALONE drawer or not (if nested inside other drawers this should be FALSE)
  drawerAccordion?: boolean; // if editor is nested inside another editor/drawer, it should show within an exapandable accordion and NOT in a standalone drawer
  intervalSetEditMode?: boolean;
  saveTooltip? : string; // for overriding with custom tooltip
  deleteTooltip? : string;// for overriding with custom tooltip
  exitTooltip? : string;// for overriding with custom tooltip
  publishTooltip? : string;// for overriding with custom tooltip
  saveText? : string; // for overriding button display text
  deleteText? : string; // for overriding button display text
  exitText? : string; // for overriding button display text
  publishText? : string; // for overriding button display text
  leaveDrawerOpenOnAction? : boolean;

}

const BottomButtons = styled("div")(() => ({
  // display: "flex",
  // border: "1pt solid lightgray",
  // marginTop: 10,
}));

const EditorWrapper: React.FC<PropsWithChildren<EditorWrapperProps>> = (
  props,
) => {
  const dispatch = useStoreDispatch();

  // for handling intervals and intervalset editing
  let intervalSetEditMode = true;
  if (typeof props.intervalSetEditMode !== "undefined") {
    intervalSetEditMode = props.intervalSetEditMode;
  }


  useEffect(() => {
    const handleKeyDown = (event:any) => {
      if (event.key === 'Enter') {
        handleSave();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Remove event listener on cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []); // Empty array ensures the effect is only run on mount and unmount


  const handleSave = async () => {
    let error = false;
    if (typeof props.save !== "undefined") {
      try {
        await props.save();
      } catch (err: any) {
        error = true;
        showError(err);
      }
    }
    if (!error) {
      const messageFunction = props.getSaveMessage;
      let message = "";
      if (typeof messageFunction === "undefined") {
        if (typeof props.table !== "undefined") {
          message = "Successfully saved " + props.table + " for " + props.title;
        } else {
          message = 'Successfully saved "' + props.title + '"';
        }
      } else {
        message = messageFunction();
      }
      enqueueSnackbar(message, { variant: "success" });
      if (props.inDrawer && !props.leaveDrawerOpenOnAction) {
        dispatch(navSliceActions.closeRightDrawer({}));
      }
    }
  };

  const handleDelete = async () => {
    let error = false;
    if (typeof props.delete !== "undefined") {
      try {
        await props.delete();
      } catch (err: any) {
        error = true;
        showError(err);
      }
    }
    if (!error) {
      const messageFunction = props.getDeleteMessage;
      let message = "";
      if (typeof messageFunction === "undefined") {
        if (typeof props.table !== "undefined") {
          message =
            "Successfully deleted " + props.table + " for " + props.title;
        } else {
          message = 'Successfully deleted "' + props.title + '"';
        }
      } else {
        message = messageFunction();
      }
      enqueueSnackbar(message, { variant: "success" });
      if (props.inDrawer && !props.leaveDrawerOpenOnAction) {
        dispatch(navSliceActions.closeRightDrawer({}));
      }
    }
  };

  const handleCancel = async () => {
    let error = false;
    if (typeof props.cancel !== "undefined") {
      try {
        await props.cancel();
      } catch (err: any) {
        error = true;
        showError(err);
      }
    }
    // we do not consider leaveDrawerOpenOnAction here
    // this in intentional because we always want to be
    // able to close the drawer manually if needed
    if (props.inDrawer && !error) {
      dispatch(navSliceActions.closeRightDrawer({}));
    }
  };

  const handleCommit = async () => {
    let error = false;
    if (typeof props.commit !== "undefined") {
      try {
        await props.commit();
      } catch (err: any) {
        error = true;
        showError(err);
      }
    }
    if (props.inDrawer  && !props.leaveDrawerOpenOnAction && !error) {
      dispatch(navSliceActions.closeRightDrawer({}));
    }
  };

  const showError = (err: any) => {
    if (typeof err === "string") {
      enqueueSnackbar(err, { variant: "error" });
    } else if (typeof err.message === "string") {
      enqueueSnackbar(err.message, { variant: "error" });
    } else {
      enqueueSnackbar("Unknown Error Occurred", { variant: "error" });
    }
  };

  const containerStyle: CSSProperties = {};
  if (props.inDrawer) {
    containerStyle.width = "850px";
    containerStyle.flexGrow = 1;
  }

  if (props.drawerAccordion === true) {
    containerStyle.border = "1pt solid lightgray";
    containerStyle.borderRadius = "5px 5px";
    containerStyle.padding = "10px";
  }

  if (!props.title) {
    containerStyle.width = "1250px";
  }

  return (
    <div style={containerStyle}>
          {props.drawerAccordion !== true && (
          <div style={{ float:"right",padding: "15px", color:"white" }}>
            <Tooltip title = {props.exitTooltip ? props.exitTooltip : "Exit Without Saving"}>
              <IconButton
                onClick={handleCancel}
                color="inherit"
                // variant="contained"
                data-testid="EditorExitWithoutSavingButton"
              >
                <CloseIcon/>
              </IconButton>
            </Tooltip>
          </div>
        )}
      {props.drawerAccordion !== true && props.title && (
        <Grid item lg={12}>
          <div
            style={{
              height: "30px",
              paddingTop: "20px",
              paddingBottom: "30px",
              paddingRight: "15px",
              paddingLeft: "15px",
              color: "white",
              backgroundColor: "#11508E",
              fontWeight: "bold",
            }}
          >
            {typeof props.table !== "undefined" ? (
              <>
                <div style={{ fontSize: "9pt", fontStyle: "italic" }}>
                  Edit {props.table} For
                </div>
                <div style={{ fontSize: "1.5em" }}>{props.title}</div>
              </>
            ) : (
              <div style={{ fontSize: "1.5em" }}>{props.title}</div>
            )}
          </div>
          <div></div>
        </Grid>
      )}
      {props.children}
      <BottomButtons>
        {props.drawerAccordion !== true && (
          <div style={{ float: "left", padding: "15px" }}>
            <Tooltip title = {props.exitTooltip ? props.exitTooltip : "Exit Without Saving"}>
              <Button
                onClick={handleCancel}
                color="warning"
                variant="contained"
                data-testid="EditorExitWithoutSavingButton"
              >
                <ArrowBackIcon />
                {props.exitText ? props.exitText : "Exit Without Saving"}
              </Button>
            </Tooltip>

          </div>
        )}
        {typeof props.delete !== "undefined" && intervalSetEditMode && (
          <div
            style={{
              float: props.drawerAccordion !== true ? "left" : "none",
              position: props.drawerAccordion !== true ? "static" : "relative",
              right: 0,
              bottom: 0,
              padding: "15px",
            }}
          >
            <Tooltip title = {props.deleteTooltip ? props.deleteTooltip : "Delete"}>
              <Button
                onClick={handleDelete}
                color="error"
                variant="contained"
                data-testid="EditorDeleteButton"
              >
                <DeleteForeverIcon />
                {props.deleteText ? props.deleteText : "Delete"}
              </Button>
            </Tooltip>

          </div>
        )}
        {typeof props.save !== "undefined" && (
          <div
            style={{
              float: props.drawerAccordion !== true ? "right" : "none",
              position: props.drawerAccordion !== true ? "static" : "relative",
              left: 650,
              bottom: 65,
              padding: "15px",
            }}
          >
            <Tooltip title = {props.saveTooltip ? props.saveTooltip : "Save"}>
              <Button
                onClick={handleSave}
                variant="contained"
                data-testid="EditorSaveButton"
              >
                <SaveIcon />
                {props.saveText ? props.saveText : "Save"}
              </Button>
            </Tooltip>
          </div>
        )}
        {typeof props.commit !== "undefined" && (
          <div
            style={{
              float: props.drawerAccordion !== true ? "right" : "none",
              padding: "15px",
            }}
          >
            <Tooltip title = {props.publishTooltip ? props.publishTooltip : "Publish"}>
              <Button
                onClick={() => {
                  handleCommit();
                }}
                color="success"
                variant="contained"
                data-testid="EditorCommitButton"
              >
                <PublishIcon />
                {props.publishText? props.publishText : "Publish"}
              </Button>
            </Tooltip >
          </div>
        )}
      </BottomButtons>
    </div>
  );
};

export default EditorWrapper;
